/**
 * Scorecard Component
 *
 * Displays the 9-round scorecard grid with player names,
 * round score breakdowns, cumulative totals, and winner highlight.
 */

import React, { useState } from 'react';
import { Scorecard as ScorecardType, RoundScoreBreakdown } from '../types';
import './Scorecard.css';

export interface ScorecardProps {
  scorecard: ScorecardType;
  currentRound: number;
  playerNames: string[];
  highlightWinner?: boolean;
}

const Scorecard: React.FC<ScorecardProps> = ({
  scorecard,
  currentRound,
  playerNames,
  highlightWinner = false,
}) => {
  const [expandedRound, setExpandedRound] = useState<number | null>(null);

  const playerCount = playerNames.length;
  const totalRounds = 9;

  // Determine winner(s) — lowest cumulative score
  const getWinnerIndices = (): number[] => {
    if (!highlightWinner) return [];
    const scores = scorecard.cumulativeScores;
    if (scores.length === 0) return [];
    const minScore = Math.min(...scores);
    return scores
      .map((score, idx) => ({ score, idx }))
      .filter(({ score }) => score === minScore)
      .map(({ idx }) => idx);
  };

  const winners = getWinnerIndices();

  const toggleRoundExpand = (roundIndex: number) => {
    setExpandedRound(expandedRound === roundIndex ? null : roundIndex);
  };

  // Render score breakdown for a round
  const renderBreakdown = (breakdown: RoundScoreBreakdown) => {
    return (
      <div className="scorecard__breakdown">
        <div className="scorecard__breakdown-row">
          <span className="scorecard__breakdown-label">Card values:</span>
          <span className="scorecard__breakdown-value">{breakdown.subtotal + (breakdown.columnCancellations.length > 0 ? breakdown.columnCancellations.reduce((sum, col) => {
            return sum + breakdown.cardValues[0][col] + breakdown.cardValues[1][col];
          }, 0) : 0)}</span>
        </div>
        {breakdown.columnCancellations.length > 0 && (
          <div className="scorecard__breakdown-row scorecard__breakdown-row--cancel">
            <span className="scorecard__breakdown-label">Column pairs cancelled:</span>
            <span className="scorecard__breakdown-value">
              −{breakdown.columnCancellations.reduce((sum, col) => {
                return sum + breakdown.cardValues[0][col] + breakdown.cardValues[1][col];
              }, 0)}
            </span>
          </div>
        )}
        {breakdown.knockPenalty > 0 && (
          <div className="scorecard__breakdown-row scorecard__breakdown-row--penalty">
            <span className="scorecard__breakdown-label">Knock penalty:</span>
            <span className="scorecard__breakdown-value">+{breakdown.knockPenalty}</span>
          </div>
        )}
        <div className="scorecard__breakdown-row scorecard__breakdown-row--total">
          <span className="scorecard__breakdown-label">Round total:</span>
          <span className="scorecard__breakdown-value">{breakdown.total}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="scorecard" role="table" aria-label="Golf game scorecard">
      {/* Header row with player names */}
      <div className="scorecard__header" role="row">
        <div className="scorecard__round-label" role="columnheader">Round</div>
        {playerNames.map((name, idx) => (
          <div
            key={idx}
            className={`scorecard__player-name ${winners.includes(idx) ? 'scorecard__player-name--winner' : ''}`}
            role="columnheader"
          >
            {name}
            {winners.includes(idx) && <span className="scorecard__winner-badge">👑</span>}
          </div>
        ))}
      </div>

      {/* Round rows */}
      <div className="scorecard__body">
        {Array.from({ length: totalRounds }, (_, roundIndex) => {
          const isCurrentRound = roundIndex === currentRound - 1;
          const isCompleted = scorecard.roundScores[0]?.[roundIndex] !== null && scorecard.roundScores[0]?.[roundIndex] !== undefined;
          const isExpanded = expandedRound === roundIndex;

          return (
            <React.Fragment key={roundIndex}>
              <div
                className={`scorecard__row ${isCurrentRound ? 'scorecard__row--current' : ''} ${isCompleted ? 'scorecard__row--completed' : ''}`}
                role="row"
                onClick={() => isCompleted ? toggleRoundExpand(roundIndex) : undefined}
                style={{ cursor: isCompleted ? 'pointer' : 'default' }}
              >
                <div className="scorecard__round-number" role="rowheader">
                  {roundIndex + 1}
                  {isCurrentRound && <span className="scorecard__current-indicator">●</span>}
                </div>
                {Array.from({ length: playerCount }, (_, playerIdx) => {
                  const roundScore = scorecard.roundScores[playerIdx]?.[roundIndex];
                  return (
                    <div
                      key={playerIdx}
                      className={`scorecard__score ${roundScore?.knockPenalty ? 'scorecard__score--penalty' : ''} ${roundScore?.columnCancellations.length ? 'scorecard__score--has-cancel' : ''}`}
                      role="cell"
                    >
                      {roundScore !== null && roundScore !== undefined ? roundScore.total : '—'}
                    </div>
                  );
                })}
              </div>

              {/* Expanded breakdown */}
              {isExpanded && isCompleted && (
                <div className="scorecard__expanded-row">
                  <div className="scorecard__expanded-spacer" />
                  {Array.from({ length: playerCount }, (_, playerIdx) => {
                    const roundScore = scorecard.roundScores[playerIdx]?.[roundIndex];
                    return (
                      <div key={playerIdx} className="scorecard__expanded-cell">
                        {roundScore && renderBreakdown(roundScore)}
                      </div>
                    );
                  })}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Cumulative totals */}
      <div className="scorecard__footer" role="row">
        <div className="scorecard__total-label" role="rowheader">Total</div>
        {scorecard.cumulativeScores.map((score, idx) => (
          <div
            key={idx}
            className={`scorecard__total ${winners.includes(idx) ? 'scorecard__total--winner' : ''}`}
            role="cell"
          >
            {score}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Scorecard;
